import { useGuestWorkplaceStore } from '@host/guest/stores/guest-workplace.store';
import { useGuestStore } from '@host/stores/guest.store';
import { useUserProfileStore } from '@host/stores/user-profile.store';
import { useWorkplaceStore } from '@host/stores/workplace.store';

export type ZohoVisitorIdentity = {
  email?: string;
  firstname?: string;
  lastname?: string;
};

function splitFullName(fullName: string): { firstname: string; lastname: string } {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstname: '', lastname: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstname: parts[0], lastname: '' };
  return { firstname: parts[0], lastname: parts.slice(1).join(' ') };
}

/** Resolve name/email for Zoho SalesIQ from the current session (platform user or guest). */
export function getZohoVisitorIdentity(): ZohoVisitorIdentity | null {
  const isGuest = localStorage.getItem('isUserGuest') === 'true';

  if (isGuest) {
    const guest = useGuestWorkplaceStore.getState().guest;
    const visitGuest = useGuestStore.getState().visitDetails?.guest as
      { name?: string; email?: string } | undefined;

    const email = (guest?.email ?? visitGuest?.email ?? '').trim();
    const fullName = (guest?.name ?? visitGuest?.name ?? '').trim();
    const { firstname, lastname } = splitFullName(fullName);

    if (!email && !firstname && !lastname) return null;
    return {
      email: email || undefined,
      firstname: firstname || undefined,
      lastname: lastname || undefined,
    };
  }

  const profile = useUserProfileStore.getState().profile;
  const workplaceProfile = useWorkplaceStore.getState().profile;

  const email = (profile?.email ?? '').trim();
  let firstname = (profile?.first_name ?? workplaceProfile?.first_name ?? '').trim();
  let lastname = (profile?.last_name ?? workplaceProfile?.last_name ?? '').trim();

  if (!firstname && !lastname) {
    const displayName = (profile?.name ?? useWorkplaceStore.getState().user?.name ?? '').trim();
    if (displayName) {
      const split = splitFullName(displayName);
      firstname = split.firstname;
      lastname = split.lastname;
    }
  }

  if (!email && !firstname && !lastname) return null;

  return {
    email: email || undefined,
    firstname: firstname || undefined,
    lastname: lastname || undefined,
  };
}

export function applyZohoVisitorIdentity(salesiq: {
  visitor?: {
    email?: (email: string) => void;
    name?: (name: { firstname?: string; lastname?: string; salutation?: string }) => void;
  };
}): void {
  const identity = getZohoVisitorIdentity();
  if (!identity || !salesiq?.visitor) return;

  try {
    if (identity.email) {
      salesiq.visitor.email?.(identity.email);
    }
    if (identity.firstname || identity.lastname) {
      salesiq.visitor.name?.({
        firstname: identity.firstname ?? '',
        lastname: identity.lastname ?? '',
      });
    }
  } catch {
    // Zoho widget may not be ready; caller can retry on open
  }
}
